import React, { useMemo } from 'react'
import { Box, Stack, Tooltip, Typography } from '@mui/material'

export const STATUS_COLORS: Record<string, string> = {
    Online: '#8BC441', // green
    Running: '#E00000', // red
    Awake: '#AA3E98', // purple
    Shutdown: '#508991', // blue / teal
    Offline: '#9AA0A6', // gray
}

const ORDER = ['Online', 'Running', 'Awake', 'Shutdown', 'Offline']

interface StatusDatum {
    boatStatus: string
    count: number
}

interface Props {
    data: StatusDatum[]
    hidden: Set<string>
    onToggle: (status: string) => void
}

/**
 * Interactive 100%-stacked fleet status bar that doubles as a show/hide legend.
 * - hover a segment → absolute count + % of fleet
 * - Online/Running/Awake emphasized; Shutdown/Offline faded
 * - click a segment or legend item → toggle it (drives map marker visibility)
 */
export const FleetStatusBar: React.FC<Props> = ({ data, hidden, onToggle }) => {
    const byStatus = useMemo(() => {
        const map = new Map(data.map((d) => [d.boatStatus, d.count]))
        return ORDER.map((s) => ({ status: s, count: map.get(s) || 0 })).filter(
            (d) => d.count > 0
        )
    }, [data])

    const total = useMemo(
        () => byStatus.reduce((sum, d) => sum + d.count, 0),
        [byStatus]
    )

    if (!total) return null

    // Hidden = not shown on the map → render faint. Visible → full. (By default
    // Shutdown/Offline are hidden, so the bar emphasizes the active statuses to
    // match the map.)
    const opacityFor = (status: string) => (hidden.has(status) ? 0.28 : 1)

    return (
        <Box>
            {/* The bar */}
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    height: 26,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                {byStatus.map((d) => {
                    const pct = (d.count / total) * 100
                    return (
                        <Tooltip
                            key={d.status}
                            arrow
                            title={
                                <Box sx={{ textAlign: 'center' }}>
                                    <strong>{d.status}</strong>
                                    <br />
                                    {d.count.toLocaleString()} ·{' '}
                                    {pct.toFixed(1)}%
                                </Box>
                            }
                        >
                            <Box
                                onClick={() => onToggle(d.status)}
                                sx={{
                                    width: `${pct}%`,
                                    backgroundColor: STATUS_COLORS[d.status],
                                    opacity: opacityFor(d.status),
                                    cursor: 'pointer',
                                    transition:
                                        'opacity 120ms ease, filter 120ms ease',
                                    '&:hover': { filter: 'brightness(1.15)' },
                                }}
                            />
                        </Tooltip>
                    )
                })}
            </Box>

            {/* Legend / toggles */}
            <Stack
                direction="row"
                flexWrap="wrap"
                sx={{ gap: 1.5, mt: 1.25 }}
            >
                {byStatus.map((d) => {
                    const isHidden = hidden.has(d.status)
                    return (
                        <Stack
                            key={d.status}
                            direction="row"
                            alignItems="center"
                            spacing={0.75}
                            onClick={() => onToggle(d.status)}
                            sx={{
                                cursor: 'pointer',
                                opacity: isHidden ? 0.4 : 1,
                                userSelect: 'none',
                            }}
                        >
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: 0.5,
                                    backgroundColor: STATUS_COLORS[d.status],
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary' }}
                            >
                                {d.status} {d.count.toLocaleString()}
                            </Typography>
                        </Stack>
                    )
                })}
            </Stack>
        </Box>
    )
}

export default FleetStatusBar
